import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabaseClient';
import {
    FaPlus, FaSearch, FaCalendarAlt, FaChevronDown, FaChevronUp, FaTimes,
    FaEllipsisV, FaRegFolderOpen, FaTrashAlt,
    FaComments, FaExclamationTriangle, FaTruck, FaSort, FaSortUp, FaSortDown,
    FaCamera, FaClock, FaCube, FaThumbtack
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getDeadlineAlert } from '../utils/deadlineUtils';
import BulkDocumentModal from '../components/BulkDocumentModal';
import CaseListScannerModal from '../components/CaseListScannerModal';
import CaseChat from '../components/CaseChat';
import { DentalImplantIcon, isCaseImplant } from '../utils/implantUtils';
import '../styles/LabWorkList.css';

const FILTER_STORAGE_KEY = 'labwork_list_filters';

const loadSavedFilters = () => {
    try {
        const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
        if (saved) {
            return JSON.parse(saved);
        }
    } catch (e) {
        console.error('Error loading filters from sessionStorage:', e);
    }
    return null;
};

const LabWorkList = () => {
    const { orders, products, clients, deleteOrder, updateOrder } = useData();
    const { isAdmin, isProducer, user } = useAuth();
    const { t, isChinese, translateStatus } = useLanguage();
    const navigate = useNavigate();

    // Load persisted filters and view state (memorization on back/navigation)
    const savedFilters = loadSavedFilters();

    const [searchTerm, setSearchTerm] = useState(() => savedFilters?.searchTerm || '');
    const [statusFilter, setStatusFilter] = useState(() => savedFilters?.statusFilter || 'All');
    const [dateFilter, setDateFilter] = useState(() => savedFilters?.dateFilter || 'Tout');
    const [selectedIds, setSelectedIds] = useState(() => {
        try {
            const saved = sessionStorage.getItem('labwork_selected_ids');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [unreadByCase, setUnreadByCase] = useState({});
    const [messagesByCase, setMessagesByCase] = useState({});
    const [pendingReplyByCase, setPendingReplyByCase] = useState({});
    const [activeChatOrder, setActiveChatOrder] = useState(null);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showScanListModal, setShowScanListModal] = useState(false);
    const [bulkDocType, setBulkDocType] = useState('BL');
    const [isBulkMinimized, setIsBulkMinimized] = useState(false);
    const [casesWith3D, setCasesWith3D] = useState(new Set());

    // State variables for sorting
    const [sortField, setSortField] = useState(() => savedFilters?.sortField !== undefined ? savedFilters.sortField : null); // 'createdAt' or 'appointmentDate'
    const [sortDirection, setSortDirection] = useState(() => savedFilters?.sortDirection || 'desc'); // 'asc' or 'desc'

    // Pagination state (default: 50 cases per page)
    const [currentPage, setCurrentPage] = useState(() => savedFilters?.currentPage || 1);
    const [itemsPerPage, setItemsPerPage] = useState(() => savedFilters?.itemsPerPage || 50);

    const isFirstRender = useRef(true);

    const handleSort = (field) => {
        if (sortField === field) {
            if (sortDirection === 'desc') {
                setSortDirection('asc');
            } else {
                setSortField(null); // 3rd click: reset to default priority sorting
                setSortDirection('desc');
            }
        } else {
            setSortField(field);
            setSortDirection('desc'); // default to newest first (descending)
        }
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) {
            return <FaSort style={{ opacity: 0.4, marginLeft: '6px', fontSize: '11px' }} />;
        }
        return sortDirection === 'asc' 
            ? <FaSortUp style={{ marginLeft: '6px', fontSize: '11px', color: '#3b82f6' }} /> 
            : <FaSortDown style={{ marginLeft: '6px', fontSize: '11px', color: '#3b82f6' }} />;
    };

    // Persist selectedIds to sessionStorage
    useEffect(() => {
        try {
            sessionStorage.setItem('labwork_selected_ids', JSON.stringify(selectedIds));
        } catch (e) {
            console.error('Error saving selectedIds to sessionStorage:', e);
        }
    }, [selectedIds]);

    // Fetch total and unread message counts per case (per-user tracking with robust pagination)
    const fetchMessagesAndUnreadCounts = useCallback(async () => {
        if (!user?.id) return;
        try {
            // 1. Get all active case IDs and statuses (safely paginated)
            let allCases = [];
            let cFrom = 0;
            const cBatch = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('cases')
                    .select('id, status, priority')
                    .range(cFrom, cFrom + cBatch - 1);
                if (error || !data || data.length === 0) break;
                allCases.push(...data);
                if (data.length < cBatch) break;
                cFrom += cBatch;
            }
            if (!allCases.length) return;

            const activeCaseIds = new Set(allCases.map(c => c.id));
            const caseStatusMap = {};
            allCases.forEach(c => {
                caseStatusMap[c.id] = (c.status || '').toLowerCase().trim();
            });

            // 2. Target sender of incoming messages (from the other party)
            const targetSender = isAdmin ? 'partner' : 'lab';

            // 3. Fetch incoming messages from targetSender (safely paginated, latest first)
            let incomingMessages = [];
            let mFrom = 0;
            const mBatch = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('case_messages')
                    .select('id, order_id, created_at')
                    .eq('sender', targetSender)
                    .order('created_at', { ascending: false })
                    .range(mFrom, mFrom + mBatch - 1);
                if (error || !data || data.length === 0) break;
                incomingMessages.push(...data);
                if (data.length < mBatch) break;
                mFrom += mBatch;
            }

            // 4. Fetch messages read by current user (safely paginated)
            let readMessages = [];
            let rFrom = 0;
            const rBatch = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('message_reads')
                    .select('message_id')
                    .eq('user_id', user.id)
                    .range(rFrom, rFrom + rBatch - 1);
                if (error || !data || data.length === 0) break;
                readMessages.push(...data);
                if (data.length < rBatch) break;
                rFrom += rBatch;
            }

            const readIds = new Set(readMessages.map(r => r.message_id));

            // Count unread incoming messages per case
            const unreadCounts = {};
            incomingMessages.forEach(msg => {
                if (activeCaseIds.has(msg.order_id) && !readIds.has(msg.id)) {
                    unreadCounts[msg.order_id] = (unreadCounts[msg.order_id] || 0) + 1;
                }
            });
            setUnreadByCase(unreadCounts);

            // 5. Fetch total message count per case (lightweight summary: id, order_id)
            let allMsgSummary = [];
            let sFrom = 0;
            const sBatch = 1000;
            while (true) {
                const { data, error } = await supabase
                    .from('case_messages')
                    .select('id, order_id')
                    .range(sFrom, sFrom + sBatch - 1);
                if (error || !data || data.length === 0) break;
                allMsgSummary.push(...data);
                if (data.length < sBatch) break;
                sFrom += sBatch;
            }

            const msgCounts = {};
            allMsgSummary.forEach(msg => {
                if (msg.order_id && activeCaseIds.has(msg.order_id)) {
                    msgCounts[msg.order_id] = (msgCounts[msg.order_id] || 0) + 1;
                }
            });
            setMessagesByCase(msgCounts);

            // Cases explicitly marked as pending reply by the user
            const pendingReplies = {};
            allCases.forEach(c => {
                const st = caseStatusMap[c.id] || '';
                const isClosed = st === 'livré' || st === 'livre' || st === 'facturé' || st === 'facture';
                if (!isClosed && c.priority === 'pending_reply') {
                    pendingReplies[c.id] = true;
                }
            });
            setPendingReplyByCase(pendingReplies);
        } catch (err) {
            console.error('Error fetching messages and unread counts:', err);
        }
    }, [user, isAdmin]);

    useEffect(() => {
        fetchMessagesAndUnreadCounts();

        // Listen for immediate read/unread events from chat
        const handleEvent = () => fetchMessagesAndUnreadCounts();
        window.addEventListener('messages-read', handleEvent);
        window.addEventListener('messages-unread', handleEvent);

        const interval = setInterval(fetchMessagesAndUnreadCounts, 30000);
        return () => {
            clearInterval(interval);
            window.removeEventListener('messages-read', handleEvent);
            window.removeEventListener('messages-unread', handleEvent);
        };
    }, [fetchMessagesAndUnreadCounts]);

    useEffect(() => {
        const fetch3DCases = async () => {
            try {
                const { data } = await supabase
                    .from('case_transfers')
                    .select('case_id, files');
                if (data && Array.isArray(data)) {
                    const idsWithFiles = new Set(
                        data.filter(t => t.files && Array.isArray(t.files) && t.files.length > 0).map(t => t.case_id)
                    );
                    setCasesWith3D(idsWithFiles);
                }
            } catch (e) {
                // Table might not exist yet
            }
        };
        fetch3DCases();
    }, []);

    // All orders are accessible to all users (including sous-traitants)
    const accessibleOrders = orders;

    // Helper: case is completed/closed when delivered or invoiced
    const isCaseClosed = (order) => {
        const st = (order?.status || '').toLowerCase().trim();
        return st === 'livré' || st === 'livre' || st === 'facturé' || st === 'facture';
    };

    // Helper: check if a case is explicitly flagged pending reply by user and still open
    const isCasePendingReply = (order) => {
        if (!order || isCaseClosed(order)) return false;
        return Boolean(pendingReplyByCase[order.id] || order.priority === 'pending_reply');
    };

    // Helper: check if a case is a Redo (cas à refaire)
    const isCaseRedo = (order) => Boolean(
        (order?.notes && order.notes.includes('[REDO')) ||
        (Array.isArray(order?.teethConfig) && order.teethConfig.some(line => line.isRedo || Number(line.quantity) === 0))
    );

    // Redo cases count
    const redoCount = accessibleOrders.filter(isCaseRedo).length;

    // Urgent cases count (deadline <= 2 days or overdue)
    const urgentCount = accessibleOrders.filter(o => getDeadlineAlert(o) !== null).length;

    // Message counts across accessible orders
    const casesWithMessagesCount = accessibleOrders.filter(o => (messagesByCase[o.id] || 0) > 0).length;
    const casesWithUnreadCount = accessibleOrders.filter(o => (unreadByCase[o.id] || 0) > 0).length;
    const totalUnreadMessagesCount = Object.values(unreadByCase).reduce((acc, count) => acc + count, 0);
    const casesWithPendingReplyCount = accessibleOrders.filter(o => isCasePendingReply(o) && !(unreadByCase[o.id] > 0)).length;

    // Filter & Search Logic
    const rawSearch = searchTerm.trim().toLowerCase();
    const cleanSearch = rawSearch.replace(/[^a-z0-9]/g, '');
    const searchDigits = rawSearch.replace(/\D/g, '');

    const filteredOrders = accessibleOrders.filter(order => {
        if (!rawSearch) return true;

        const caseNum = String(order.caseNumber || '').toLowerCase();
        const caseId = String(order.id || '').toLowerCase();
        const patient = String(order.patientName || '').toLowerCase();
        const practitioner = String(order.practitionerName || '').toLowerCase();
        const ref = String(order.patientReference || '').toLowerCase();
        const notes = String(order.notes || '').toLowerCase();

        // 1. Direct text include
        if (
            caseNum.includes(rawSearch) ||
            caseId.includes(rawSearch) ||
            patient.includes(rawSearch) ||
            practitioner.includes(rawSearch) ||
            ref.includes(rawSearch) ||
            notes.includes(rawSearch)
        ) {
            return true;
        }

        // 2. Normalized alphanumeric match (ignores spaces, dashes, etc.)
        const cleanNum = caseNum.replace(/[^a-z0-9]/g, '');
        const cleanId = caseId.replace(/[^a-z0-9]/g, '');
        if (cleanSearch && (cleanNum.includes(cleanSearch) || cleanId.includes(cleanSearch))) {
            return true;
        }

        // 3. ENO case number numeric match (e.g. "eno 4", "eno-4", "eno04", "04", "4")
        if (searchDigits) {
            const numVal = parseInt(searchDigits, 10);
            const caseNumDigits = caseNum.replace(/\D/g, '');
            const caseIdDigits = caseId.replace(/\D/g, '');

            if (caseNumDigits && parseInt(caseNumDigits, 10) === numVal) return true;
            if (caseIdDigits && parseInt(caseIdDigits, 10) === numVal) return true;
        }

        // 4. 3D / STL keyword match
        if ((rawSearch === '3d' || rawSearch === 'stl') && casesWith3D.has(order.id)) {
            return true;
        }

        return false;
    }).filter(order => {
        let matchesStatus = true;
        if (statusFilter === 'urgent') {
            matchesStatus = getDeadlineAlert(order) !== null;
        } else if (statusFilter === 'unread') {
            matchesStatus = (unreadByCase[order.id] || 0) > 0;
        } else if (statusFilter === 'messages') {
            matchesStatus = (messagesByCase[order.id] || 0) > 0;
        } else if (statusFilter === 'redo') {
            matchesStatus = isCaseRedo(order);
        } else if (statusFilter !== 'All') {
            matchesStatus = order.status?.toLowerCase() === statusFilter.toLowerCase();
        }

        let matchesDate = true;
        // Never hide cases with unread messages by date filter
        if (statusFilter !== 'unread') {
            if (dateFilter === '7 derniers jours') {
                const dateObj = new Date(order.createdAt);
                const limit = new Date();
                limit.setDate(limit.getDate() - 7);
                matchesDate = dateObj >= limit;
            } else if (dateFilter === '30 derniers jours') {
                const dateObj = new Date(order.createdAt);
                const limit = new Date();
                limit.setDate(limit.getDate() - 30);
                matchesDate = dateObj >= limit;
            }
        }

        return matchesStatus && matchesDate;
    }).sort((a, b) => {
        // If searching with numbers, float exact ENO number match to the top
        if (searchDigits) {
            const numVal = parseInt(searchDigits, 10);
            const aNum = parseInt(String(a.caseNumber || a.id || '').replace(/\D/g, ''), 10);
            const bNum = parseInt(String(b.caseNumber || b.id || '').replace(/\D/g, ''), 10);

            if (aNum === numVal && bNum !== numVal) return -1;
            if (bNum === numVal && aNum !== numVal) return 1;
        }

        // 1. If user explicitly sorted by a column header ("Date de commande" or "Livraison demandée"):
        // The table strictly follows the chosen column sort across ALL rows
        if (sortField) {
            const valA = sortField === 'createdAt' ? a.createdAt : a.appointmentDate;
            const valB = sortField === 'createdAt' ? b.createdAt : b.appointmentDate;

            // Falsy/missing values are always put at the bottom
            if (!valA && valB) return 1;
            if (valA && !valB) return -1;
            if (!valA && !valB) return 0;

            const dateA = new Date(valA);
            const dateB = new Date(valB);

            if (sortDirection === 'asc') {
                return dateA - dateB;
            } else {
                return dateB - dateA;
            }
        }

        // 2. Default Smart Sorting (when no explicit column sort is chosen):
        // Priority A: Cases with unread messages ALWAYS float to the very top!
        const unreadA = unreadByCase[a.id] || 0;
        const unreadB = unreadByCase[b.id] || 0;
        if (unreadA > 0 && unreadB === 0) return -1;
        if (unreadB > 0 && unreadA === 0) return 1;
        if (unreadA !== unreadB) return unreadB - unreadA;

        // Priority B: Cases pending reply
        const pendingA = isCasePendingReply(a) ? 1 : 0;
        const pendingB = isCasePendingReply(b) ? 1 : 0;
        if (pendingA !== pendingB) return pendingB - pendingA;

        // Priority C: Urgent orders float to the top
        const alertA = getDeadlineAlert(a);
        const alertB = getDeadlineAlert(b);

        if (alertA && !alertB) return -1;
        if (!alertA && alertB) return 1;
        if (alertA && alertB) {
            if (alertA.priority !== alertB.priority) {
                return alertA.priority - alertB.priority;
            }
            return alertA.days - alertB.days;
        }

        // Priority D: Most recent orders first
        return new Date(b.createdAt) - new Date(a.createdAt);
    });

    // Persist filters, sorting, and pagination to sessionStorage
    useEffect(() => {
        try {
            sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
                searchTerm,
                statusFilter,
                dateFilter,
                sortField,
                sortDirection,
                currentPage,
                itemsPerPage
            }));
        } catch (e) {
            console.error('Error saving filters to sessionStorage:', e);
        }
    }, [searchTerm, statusFilter, dateFilter, sortField, sortDirection, currentPage, itemsPerPage]);

    // Reset to first page whenever filters or sorting changes (skip on initial mount to preserve saved page)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setCurrentPage(1);
    }, [searchTerm, statusFilter, dateFilter, sortField, sortDirection, itemsPerPage]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, filteredOrders.length);
    const paginatedOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

    // List of cases with messages (in filtered order) for sequential navigation in chat
    const casesWithMessagesList = filteredOrders.filter(o => (messagesByCase[o.id] || 0) > 0);
    const currentChatIndex = activeChatOrder
        ? casesWithMessagesList.findIndex(o => o.id === activeChatOrder.id)
        : -1;
    const hasPreviousCase = currentChatIndex > 0;
    const hasNextCase = currentChatIndex >= 0 && currentChatIndex < casesWithMessagesList.length - 1;

    const handlePreviousCase = () => {
        if (hasPreviousCase) {
            setActiveChatOrder(casesWithMessagesList[currentChatIndex - 1]);
        }
    };

    const handleNextCase = () => {
        if (hasNextCase) {
            setActiveChatOrder(casesWithMessagesList[currentChatIndex + 1]);
        }
    };

    const getPageNumbers = (current, total) => {
        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }
        if (current <= 4) {
            return [1, 2, 3, 4, 5, '...', total];
        }
        if (current >= total - 3) {
            return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
        }
        return [1, '...', current - 1, current, current + 1, '...', total];
    };

    const [lastSelectedId, setLastSelectedId] = useState(null);

    const isAllPageSelected = paginatedOrders.length > 0 && paginatedOrders.every(o => selectedIds.includes(o.id));

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(prev => Array.from(new Set([...prev, ...paginatedOrders.map(o => o.id)])));
        } else {
            const pageIds = new Set(paginatedOrders.map(o => o.id));
            setSelectedIds(prev => prev.filter(id => !pageIds.has(id)));
        }
        setLastSelectedId(null);
    };

    const handleSelectItem = (e, id) => {
        e.stopPropagation();
        const isShiftPressed = Boolean(e.nativeEvent?.shiftKey || e.shiftKey);

        if (isShiftPressed && lastSelectedId && lastSelectedId !== id) {
            const currentIndex = filteredOrders.findIndex(o => o.id === id);
            const lastIndex = filteredOrders.findIndex(o => o.id === lastSelectedId);

            if (currentIndex !== -1 && lastIndex !== -1) {
                const start = Math.min(currentIndex, lastIndex);
                const end = Math.max(currentIndex, lastIndex);

                const idsInRange = filteredOrders.slice(start, end + 1).map(o => o.id);
                const isTargetChecking = e.target.checked;

                setSelectedIds(prev => {
                    if (isTargetChecking) {
                        return Array.from(new Set([...prev, ...idsInRange]));
                    } else {
                        return prev.filter(selectedId => !idsInRange.includes(selectedId));
                    }
                });
            }
        } else {
            setSelectedIds(prev =>
                prev.includes(id)
                    ? prev.filter(item => item !== id)
                    : [...prev, id]
            );
        }

        setLastSelectedId(id);
    };

    const handleBulkDelete = () => {
        if (window.confirm(`Voulez-vous vraiment supprimer les ${selectedIds.length} cas sélectionnés ?`)) {
            selectedIds.forEach(id => deleteOrder(id));
            setSelectedIds([]);
        }
    };

    const handleBulkStatusChange = async (newStatus) => {
        if (!newStatus || selectedIds.length === 0) return;
        if (window.confirm(`Voulez-vous passer les ${selectedIds.length} cas sélectionnés au statut "${newStatus}" ?`)) {
            try {
                await Promise.all(selectedIds.map(id => updateOrder(id, { status: newStatus })));
                setSelectedIds([]);
            } catch (error) {
                console.error("Erreur lors de la mise à jour des statuts:", error);
                alert("Erreur lors de la mise à jour de certains cas.");
            }
        }
    };

    const getStatusClass = (status) => {
        const s = status ? status.toLowerCase() : '';
        if (s === 'reçu' || s === 'recu') return 'badge-status-received';
        if (s === 'facturé' || s === 'facture') return 'badge-status-invoiced';
        if (s === 'livré') return 'badge-status-completed';
        if (s === 'en attente' || s === 'terminé') return 'badge-status-onhold';
        if (s === 'en cours' || s === 'production') return 'badge-status-progress';
        return 'badge-status-received';
    };

    const formatStatusTime = (order) => {
        if (!order) return null;
        const s = (order.status || '').toLowerCase();
        if (s === 'livré' || s === 'livre') {
            const d = order.deliveryDate || order.delivery_date;
            if (d) {
                const dateObj = new Date(d);
                const dateStr = dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                const timeStr = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                return { text: `${dateStr} à ${timeStr}`, isDelivered: true };
            }
        }
        if (s === 'facturé' || s === 'facture') {
            const d = order.invoicedAt || order.invoiced_at;
            if (d) {
                const dateObj = new Date(d);
                const dateStr = dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                const timeStr = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                return { text: `${dateStr} à ${timeStr}`, isInvoiced: true };
            }
        }
        if (order.statusHistory && Array.isArray(order.statusHistory) && order.statusHistory.length > 0) {
            const matchingEntry = [...order.statusHistory].reverse().find(h => (h.status || '').toLowerCase() === s);
            if (matchingEntry && matchingEntry.date) {
                const dateObj = new Date(matchingEntry.date);
                const dateStr = dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                const timeStr = dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                return { text: `${dateStr} à ${timeStr}` };
            }
        }
        return null;
    };

    return (
        <div className="case-box-page">
            <div className="case-box-header">
                <div className="title-section">
                    <h2>Case Box</h2>
                    <p>{isProducer ? (isChinese ? '查看生产工单并与技工所实时沟通。' : 'Consultez les travaux et posez vos questions.') : 'Gérez et suivez l\'état de tous vos cas clients.'}</p>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    {isAdmin && (
                        <button
                            type="button"
                            className="btn btn-outline-primary fw-bold d-inline-flex align-items-center gap-2 px-3 py-2 shadow-sm"
                            onClick={() => setShowScanListModal(true)}
                            title="Scanner une capture d'écran ou coller une liste de cas pour tout sélectionner d'un coup"
                            style={{
                                borderRadius: '10px',
                                fontSize: '13.5px',
                                border: '1.5px solid #2563eb',
                                backgroundColor: '#ffffff',
                                color: '#2563eb'
                            }}
                        >
                            <FaCamera /> Scan List
                        </button>
                    )}
                    {isAdmin && (
                        <button className="btn-new-case" onClick={() => navigate('/jobs/new')}>
                            <FaPlus className="me-2" /> Nouveau cas
                        </button>
                    )}
                </div>
            </div>

            {/* Banner for urgent cases close to delivery date */}
            {urgentCount > 0 && statusFilter !== 'urgent' && (
                <div className="urgent-banner shadow-sm mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <FaExclamationTriangle className="text-danger flex-shrink-0" size={18} />
                        <span>
                            <strong>{urgentCount} {isChinese ? '个生产中工单' : 'cas en production'}</strong> {isChinese ? '距要求交货时间不足48小时或已逾期！已置顶显示。' : (urgentCount > 1 ? 'arrivent' : 'arrive') + ' à échéance dans moins de 48h ou en retard ! Ils sont placés en tête de liste.'}
                        </span>
                    </div>
                    <button
                        className="btn btn-sm btn-danger px-3 fw-bold ms-auto"
                        onClick={() => setStatusFilter('urgent')}
                        style={{ borderRadius: '8px', fontSize: '12px' }}
                    >
                        {isChinese ? `筛选加急工单 (${urgentCount})` : `Filtrer les cas prioritaires (${urgentCount})`}
                    </button>
                </div>
            )}

            {/* Mobile Horizontal Status Tabs */}
            <div className="status-tabs-mobile">
                {[
                    { id: 'All', label: t('common.all', 'Tous') },
                    ...(totalUnreadMessagesCount > 0 ? [{ id: 'unread', label: `🔴 ${isChinese ? '未读' : 'Non lus'} (${totalUnreadMessagesCount})` }] : []),
                    ...(urgentCount > 0 ? [{ id: 'urgent', label: `🚨 ${t('common.urgent', 'Urgents')} (${urgentCount})` }] : []),
                    ...(casesWithMessagesCount > 0 ? [{
                        id: 'messages',
                        label: `💬 Messages (${casesWithMessagesCount})${totalUnreadMessagesCount > 0 ? ` 🔴 ${totalUnreadMessagesCount}` : casesWithPendingReplyCount > 0 ? ` ⏳ ${casesWithPendingReplyCount}` : ''}`
                    }] : []),
                    ...(redoCount > 0 ? [{ id: 'redo', label: `🔄 ${isChinese ? '返工' : 'Redos'} (${redoCount})` }] : []),
                    { id: 'reçu', label: t('status.recu', 'Reçu') },
                    { id: 'production', label: t('status.production', 'Production') },
                    { id: 'en attente', label: t('status.en_attente', 'En attente') },
                    { id: 'livré', label: t('status.livre', 'Livré') },
                    { id: 'facturé', label: t('status.facture', 'Facturé') }
                ].map(tab => (
                    <button
                        key={tab.id}
                        className={`status-tab-item ${statusFilter === tab.id ? 'active' : ''} ${tab.id === 'unread' ? 'tab-unread' : ''}`}
                        onClick={() => setStatusFilter(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="case-box-filters">
                <div className="filter-left">
                    <div className="filter-item">
                        <FaCalendarAlt className="filter-icon" />
                        <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                            <option value="Tout">{isChinese ? '全部时间' : 'Tout'}</option>
                            <option value="7 derniers jours">{isChinese ? '最近 7 天' : '7 derniers jours'}</option>
                            <option value="30 derniers jours">{isChinese ? '最近 30 天' : '30 derniers jours'}</option>
                        </select>
                        <FaChevronDown className="chevron-icon" />
                    </div>
                    {/* Hide desktop select on mobile */}
                    <div className="filter-item filter-status-desktop">
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                            <option value="All">{t('status.all', 'Tous les statuts')}</option>
                            {totalUnreadMessagesCount > 0 && (
                                <option value="unread" style={{ fontWeight: 'bold', color: '#dc2626' }}>
                                    🔴 {isChinese ? '未读消息' : 'Messages non lus'} ({totalUnreadMessagesCount}) • {casesWithUnreadCount} {isChinese ? '个工单' : 'cas'}
                                </option>
                            )}
                            {urgentCount > 0 && <option value="urgent">🚨 {isChinese ? '加急 / 临近出货' : 'Urgents / Délais proches'} ({urgentCount})</option>}
                            {casesWithMessagesCount > 0 && (
                                <option value="messages">
                                    💬 {isChinese ? '含沟通消息' : 'Avec messages'} ({casesWithMessagesCount}){totalUnreadMessagesCount > 0 ? ` • ${totalUnreadMessagesCount} ${isChinese ? '条未读' : 'non lu(s)'}` : ''}{casesWithPendingReplyCount > 0 ? ` • ${casesWithPendingReplyCount} ${isChinese ? '等待回复' : 'en attente'}` : ''}
                                </option>
                            )}
                            {redoCount > 0 && (
                                <option value="redo" style={{ fontWeight: 'bold', color: '#d97706' }}>
                                    🔄 {isChinese ? '返工重做' : 'Cas à refaire / Redos'} ({redoCount})
                                </option>
                            )}
                            <option value="reçu">{t('status.recu', 'Reçu')}</option>
                            <option value="production">{t('status.production', 'En production')}</option>
                            <option value="en attente">{t('status.en_attente', 'En attente')}</option>
                            <option value="livré">{t('status.livre', 'Livré')}</option>
                            <option value="facturé">{t('status.facture', 'Facturé')}</option>
                        </select>
                        <FaChevronDown className="chevron-icon" />
                    </div>

                    {/* Quick Button for UNREAD Messages */}
                    {totalUnreadMessagesCount > 0 && (
                        <button
                            type="button"
                            className={`btn btn-sm d-flex align-items-center gap-2 fw-bold px-3 ${statusFilter === 'unread' ? 'btn-danger shadow text-white' : 'btn-outline-danger bg-white'}`}
                            style={{
                                borderRadius: '8px',
                                height: '38px',
                                border: '1.5px solid #ef4444',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap',
                                animation: statusFilter !== 'unread' ? 'badge-pulse 2s ease-in-out infinite' : 'none'
                            }}
                            onClick={() => setStatusFilter(statusFilter === 'unread' ? 'All' : 'unread')}
                            title={statusFilter === 'unread' ? "Afficher tous les cas" : "Filtrer uniquement les cas avec messages non lus"}
                        >
                            <FaComments size={14} />
                            <span>{isChinese ? '未读' : 'Non lus'} ({totalUnreadMessagesCount})</span>
                            <span className={`badge ${statusFilter === 'unread' ? 'bg-white text-danger' : 'bg-danger text-white'} fw-bold ms-1`} style={{ fontSize: '10px' }}>
                                {casesWithUnreadCount} {isChinese ? '单' : 'cas'}
                            </span>
                        </button>
                    )}

                    {/* Quick Button for Cases with Messages */}
                    {casesWithMessagesCount > 0 && (
                        <button
                            type="button"
                            className={`btn btn-sm d-flex align-items-center gap-2 fw-semibold px-3 ${statusFilter === 'messages' ? 'btn-primary shadow-sm text-white' : 'btn-outline-primary bg-white'}`}
                            style={{
                                borderRadius: '8px',
                                height: '38px',
                                border: '1.5px solid #3b82f6',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap'
                            }}
                            onClick={() => setStatusFilter(statusFilter === 'messages' ? 'All' : 'messages')}
                            title={statusFilter === 'messages' ? "Désactiver le filtre messages" : "Filtrer uniquement les cas avec des messages"}
                        >
                            <FaComments size={14} />
                            <span>Messages ({casesWithMessagesCount})</span>
                            {casesWithPendingReplyCount > 0 && (
                                <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '10px' }} title={`${casesWithPendingReplyCount} cas en attente de réponse`}>
                                    {casesWithPendingReplyCount} {isChinese ? '待回复' : 'en attente'}
                                </span>
                            )}
                        </button>
                    )}
                </div>
                <div className="filter-right">
                    <div className="search-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder={isChinese ? "搜索患者姓名、医生、工单号..." : "Nom du patient, Praticien, ID..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Mobile Card List View */}
            <div className="cases-cards-mobile">
                {paginatedOrders.length === 0 ? (
                    <div className="empty-state">
                        <FaRegFolderOpen size={48} />
                        <p>{t('labwork.no_cases_found', 'Aucun cas trouvé correspondant à vos critères.')}</p>
                    </div>
                ) : (
                    paginatedOrders.map(order => {
                        const deadline = getDeadlineAlert(order);
                        const caseNumUpper = (order.caseNumber || '').toUpperCase();
                        const caseDigits = (caseNumUpper.match(/\d+/) || [])[0];
                        const isMilestone100 = caseNumUpper === 'ENO-100' || caseNumUpper.endsWith('-100') || caseDigits === '100';
                        const isMilestone300 = caseNumUpper === 'ENO-300' || caseNumUpper.endsWith('-300') || caseDigits === '300';
                        return (
                            <div
                                key={order.id}
                                className={`case-mobile-card ${selectedIds.includes(order.id) ? 'selected' : ''} ${deadline ? 'urgent-border' : ''} ${isMilestone300 ? 'milestone-rocket-card' : isMilestone100 ? 'milestone-gold-card' : ''} ${unreadByCase[order.id] > 0 ? 'has-unread-case' : ''}`}
                                onClick={() => navigate(`/jobs/${order.id}`)}
                            >
                                <div className="card-top-row">
                                    <div className="status-badge-container">
                                        <span className={`status-pill ${getStatusClass(order.status)}`}>
                                            {translateStatus(order.status) || 'Pending'}
                                        </span>
                                        {(() => {
                                            const timeInfo = formatStatusTime(order);
                                            if (!timeInfo) return null;
                                            return (
                                                <span className={`status-sub-date ${timeInfo.isDelivered ? 'delivered' : ''} ${timeInfo.isInvoiced ? 'invoiced' : ''}`}>
                                                    {timeInfo.isDelivered && <FaTruck size={9} className="me-1" />}
                                                    {timeInfo.text}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    {deadline && (
                                        <span className={`deadline-badge-pill ${deadline.badgeClass}`}>
                                            <FaExclamationTriangle size={10} className="me-1" />
                                            {deadline.shortLabel}
                                        </span>
                                    )}
                                    <span className={`card-case-number ${isMilestone300 ? 'rocket-case-badge' : isMilestone100 ? 'gold-case-badge' : ''}`}>
                                        {isMilestone300 ? `🚀 ${order.caseNumber || 'ENO-300'}` : isMilestone100 ? `🏆 ${order.caseNumber || 'ENO-100'}` : (order.caseNumber || `#${order.id.substring(0, 8).toUpperCase()}`)}
                                    </span>
                                    {isCaseRedo(order) && (
                                        <span className="badge bg-warning text-dark border border-warning ms-1 fw-bold" style={{ fontSize: '10px' }}>
                                            🔄 REDO
                                        </span>
                                    )}
                                    {casesWith3D.has(order.id) && (
                                        <span className="badge bg-primary-subtle text-primary border border-primary-subtle ms-1 fw-bold" style={{ fontSize: '10px' }}>
                                            <FaCube className="me-1" size={9} />3D
                                        </span>
                                    )}
                                    {isCaseImplant(order, products) && (
                                        <span
                                            className="badge ms-1 fw-bold"
                                            style={{
                                                fontSize: '10px',
                                                backgroundColor: '#f3e8ff',
                                                color: '#7e22ce',
                                                border: '1px solid #d8b4fe'
                                            }}
                                            title="Travail sur implant"
                                        >
                                            <DentalImplantIcon size={10} className="me-1" />
                                            Implant
                                        </span>
                                    )}
                                </div>
                                <div className="card-patient-name">
                                    {isMilestone300 ? <span className="me-1">🚀</span> : isMilestone100 ? <span className="me-1">🏆</span> : null}
                                    {order.patientName?.toUpperCase()}
                                    {(order.patientReference || order.patient_reference) && (
                                        <span className="ms-2 badge bg-light text-secondary border fw-normal" style={{ fontSize: '11px' }}>Réf: {order.patientReference || order.patient_reference}</span>
                                    )}
                                    {unreadByCase[order.id] > 0 ? (
                                        <span
                                            className="case-unread-badge"
                                            title={`${unreadByCase[order.id]} message(s) non lu(s) - Cliquer pour ouvrir`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveChatOrder(order);
                                            }}
                                        >
                                            <FaComments size={11} />
                                            <span>{unreadByCase[order.id]} {isChinese ? '条未读' : 'non lu(s)'}</span>
                                        </span>
                                    ) : isCasePendingReply(order) ? (
                                        <span
                                            className="case-pending-reply-badge"
                                            title="Réponse en attente de votre part - Cliquer pour répondre"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveChatOrder(order);
                                            }}
                                        >
                                            <FaClock size={10} />
                                            <span>{isChinese ? '待回复' : 'En attente'}</span>
                                        </span>
                                    ) : (statusFilter === 'messages' && messagesByCase[order.id] > 0) ? (
                                        <span
                                            className="case-msg-count-badge"
                                            title={`${messagesByCase[order.id]} message(s) au total - Cliquer pour ouvrir`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveChatOrder(order);
                                            }}
                                        >
                                            <FaComments size={10} />
                                            <span>{messagesByCase[order.id]}</span>
                                        </span>
                                    ) : null}
                                </div>
                                <div className="card-doctor-name">
                                    Dr. {order.practitionerName}
                                </div>
                                <div className="card-delivery-date">
                                    {isChinese ? '预计交货：' : 'Livraison demandée : '}{order.appointmentDate ? new Date(order.appointmentDate).toLocaleDateString(isChinese ? 'zh-CN' : 'fr-FR') : '-'}
                                </div>
                                {/* Checkbox for Admin selection on mobile */}
                                {isAdmin && (
                                    <div className="card-checkbox-wrapper" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(order.id)}
                                            onChange={(e) => handleSelectItem(e, order.id)}
                                        />
                                        <span className="checkbox-label">Sélectionner</span>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Desktop Table View */}
            <div className="case-list-container desktop-only-view">
                <div className={`case-list-header ${isProducer ? 'producer-grid' : ''}`}>
                    {isAdmin && (
                        <div className="col-check">
                            <input
                                type="checkbox"
                                checked={isAllPageSelected}
                                onChange={handleSelectAll}
                                title={isAllPageSelected ? "Désélectionner la page" : "Sélectionner tous les cas de la page"}
                            />
                        </div>
                    )}
                    <div className="col-statut">{t('labwork.col_status', 'Statut')}</div>
                    <div className="col-name">{isChinese ? '制作项目' : 'Nom du cas'}</div>
                    <div className="col-patient">{t('labwork.col_patient', 'Nom du patient')}</div>
                    <div 
                        className={`col-date sortable-header ${sortField === 'createdAt' ? 'active' : ''}`}
                        onClick={() => handleSort('createdAt')}
                    >
                        {(isChinese ? '下单日期' : 'Date de commande') + ' '}{renderSortIcon('createdAt')}
                    </div>
                    <div 
                        className={`col-delivery sortable-header ${sortField === 'appointmentDate' ? 'active' : ''}`}
                        onClick={() => handleSort('appointmentDate')}
                    >
                        {(isChinese ? '要求交货' : 'Livraison demandée') + ' '}{renderSortIcon('appointmentDate')}
                    </div>
                    <div className="col-clinic">{isChinese ? '门诊 / 医生' : 'Cabinet Dentaire'}</div>
                    <div className="col-id">{t('labwork.col_number', 'N° Dossier')}</div>
                </div>

                <div className="case-list-body">
                    {paginatedOrders.length === 0 ? (
                        <div className="empty-state">
                            <FaRegFolderOpen size={48} />
                            <p>{t('labwork.no_cases_found', 'Aucun cas trouvé correspondant à vos critères.')}</p>
                        </div>
                    ) : (
                        paginatedOrders.map(order => {
                            const deadline = getDeadlineAlert(order);
                            const caseNumUpper = (order.caseNumber || '').toUpperCase();
                            const caseDigits = (caseNumUpper.match(/\d+/) || [])[0];
                            const isMilestone100 = caseNumUpper === 'ENO-100' || caseNumUpper.endsWith('-100') || caseDigits === '100';
                            const isMilestone300 = caseNumUpper === 'ENO-300' || caseNumUpper.endsWith('-300') || caseDigits === '300';
                            return (
                                <div
                                    className={`case-row ${selectedIds.includes(order.id) ? 'selected' : ''} ${isProducer ? 'producer-grid' : ''} ${deadline ? 'urgent-border' : ''} ${isMilestone300 ? 'milestone-rocket-row' : isMilestone100 ? 'milestone-gold-row' : ''} ${unreadByCase[order.id] > 0 ? 'has-unread-case' : ''}`}
                                    key={order.id}
                                    onClick={() => navigate(`/jobs/${order.id}`)}
                                >
                                    {isAdmin && (
                                        <div className="col-check" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(order.id)}
                                                onChange={(e) => handleSelectItem(e, order.id)}
                                            />
                                        </div>
                                    )}
                                    <div className="col-statut">
                                        <span className={`status-pill ${getStatusClass(order.status)}`}>
                                            {translateStatus(order.status) || 'Pending'}
                                        </span>
                                        {(() => {
                                            const timeInfo = formatStatusTime(order);
                                            if (!timeInfo) return null;
                                            return (
                                                <span className={`status-sub-date ${timeInfo.isDelivered ? 'delivered' : ''} ${timeInfo.isInvoiced ? 'invoiced' : ''}`} title={`Statut mis à jour ${timeInfo.text}`}>
                                                    {timeInfo.isDelivered && <FaTruck size={9} className="me-1" />}
                                                    {timeInfo.text}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <div className="col-name text-truncate">
                                        {isMilestone300 ? <span className="me-1">🚀</span> : isMilestone100 ? <span className="me-1">🏆</span> : null}
                                        Cas de {order.patientName}
                                        {isCaseRedo(order) && (
                                            <span className="badge bg-warning text-dark border border-warning ms-2 fw-bold" style={{ fontSize: '11px', verticalAlign: 'middle' }}>
                                                🔄 REDO (0 €)
                                            </span>
                                        )}
                                        {casesWith3D.has(order.id) && (
                                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle ms-2 fw-bold" style={{ fontSize: '11px', verticalAlign: 'middle' }} title="Fichiers 3D disponibles (TransferEno)">
                                                <FaCube className="me-1" size={10} />3D
                                            </span>
                                        )}
                                        {isCaseImplant(order, products) && (
                                            <span
                                                className="badge ms-2 fw-bold"
                                                style={{
                                                    fontSize: '11px',
                                                    verticalAlign: 'middle',
                                                    backgroundColor: '#f3e8ff',
                                                    color: '#7e22ce',
                                                    border: '1px solid #d8b4fe'
                                                }}
                                                title="Cas sur Implant / Implantologie"
                                            >
                                                <DentalImplantIcon size={11} className="me-1" />
                                                Implant
                                            </span>
                                        )}
                                        {!isProducer && order.internalNotes && (
                                            <span
                                                className="badge border ms-2 fw-semibold"
                                                style={{ fontSize: '11px', verticalAlign: 'middle', cursor: 'help', backgroundColor: '#fef9c3', color: '#854d0e', borderColor: '#fde047' }}
                                                title={`Mémo interne : ${order.internalNotes}`}
                                            >
                                                <FaThumbtack className="me-1 text-danger" size={10} />
                                                Mémo
                                            </span>
                                        )}
                                        {unreadByCase[order.id] > 0 ? (
                                            <span
                                                className="case-unread-badge"
                                                title={`${unreadByCase[order.id]} message(s) non lu(s) - Cliquer pour ouvrir la discussion`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveChatOrder(order);
                                                }}
                                            >
                                                <FaComments size={11} />
                                                <span>{unreadByCase[order.id]} non lu{unreadByCase[order.id] > 1 ? 's' : ''}</span>
                                            </span>
                                        ) : isCasePendingReply(order) ? (
                                            <span
                                                className="case-pending-reply-badge"
                                                title="Réponse en attente de votre part - Cliquer pour répondre"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveChatOrder(order);
                                                }}
                                            >
                                                <FaClock size={10} />
                                                <span>En attente</span>
                                            </span>
                                        ) : (statusFilter === 'messages' && messagesByCase[order.id] > 0) ? (
                                            <span
                                                className="case-msg-count-badge"
                                                title={`${messagesByCase[order.id]} message(s) au total - Cliquer pour ouvrir`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setActiveChatOrder(order);
                                                }}
                                            >
                                                <FaComments size={10} />
                                                <span>{messagesByCase[order.id]}</span>
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className="col-patient text-truncate">
                                        {order.patientName}
                                        {(order.patientReference || order.patient_reference) ? <span className="small text-muted d-block" style={{ fontSize: '11px' }}>Réf: {order.patientReference || order.patient_reference}</span> : null}
                                    </div>
                                    <div className="col-date">{new Date(order.createdAt).toLocaleDateString()}</div>
                                    <div className="col-delivery d-flex align-items-center">
                                        <span>{order.appointmentDate ? new Date(order.appointmentDate).toLocaleDateString() : '-'}</span>
                                        {deadline && (
                                            <span className={`deadline-badge-pill ms-2 ${deadline.badgeClass}`} title={deadline.label}>
                                                <FaExclamationTriangle size={10} className="me-1" />
                                                {deadline.shortLabel}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-clinic text-truncate">Dr. {order.practitionerName}</div>
                                    <div className="col-id">
                                        {isMilestone300 ? (
                                            <span className="rocket-case-badge" title="Dossier historique n°300">
                                                🚀 {order.caseNumber || 'ENO-300'}
                                            </span>
                                        ) : isMilestone100 ? (
                                            <span className="gold-case-badge" title="Dossier historique n°100">
                                                🏆 {order.caseNumber || 'ENO-100'}
                                            </span>
                                        ) : (
                                            order.caseNumber || `#${order.id.substring(0, 8).toUpperCase()}`
                                        )}
                                    </div>
                                    <div className="row-actions">
                                        <FaEllipsisV />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Pagination & Footer Bar */}
            <div className="case-box-footer d-flex flex-wrap align-items-center justify-content-between gap-3 py-3 px-2">
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    <span className="text-secondary fw-semibold" style={{ fontSize: '13.5px' }}>
                        {filteredOrders.length === 0 ? (
                            'Aucun cas trouvé'
                        ) : (
                            <>
                                Affichage de <strong className="text-dark">{startIndex + 1}</strong> à <strong className="text-dark">{endIndex}</strong> sur <strong className="text-primary">{filteredOrders.length}</strong> cas
                            </>
                        )}
                    </span>
                    <div className="d-inline-flex align-items-center gap-2">
                        <label className="text-muted small m-0" htmlFor="cases-per-page">Par page :</label>
                        <select
                            id="cases-per-page"
                            className="form-select form-select-sm"
                            style={{ width: 'auto', borderRadius: '8px', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}
                            value={itemsPerPage}
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value={25}>25 cas</option>
                            <option value={50}>50 cas</option>
                            <option value={100}>100 cas</option>
                            <option value={999999}>Tous les cas</option>
                        </select>
                    </div>
                </div>

                {totalPages > 1 && (
                    <div className="pagination-controls d-flex align-items-center gap-1">
                        <button
                            className="btn btn-sm btn-outline-secondary pagination-btn"
                            disabled={validCurrentPage === 1}
                            onClick={() => {
                                setCurrentPage(prev => Math.max(1, prev - 1));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        >
                            &laquo; Précédent
                        </button>

                        {getPageNumbers(validCurrentPage, totalPages).map((p, idx) => {
                            if (p === '...') {
                                return <span key={`ellipsis-${idx}`} className="px-2 text-muted fw-bold">...</span>;
                            }
                            return (
                                <button
                                    key={p}
                                    className={`btn btn-sm ${validCurrentPage === p ? 'btn-primary fw-bold text-white shadow-sm' : 'btn-outline-secondary'} pagination-btn`}
                                    style={{ minWidth: '34px', borderRadius: '6px', fontSize: '13px' }}
                                    onClick={() => {
                                        setCurrentPage(p);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                >
                                    {p}
                                </button>
                            );
                        })}

                        <button
                            className="btn btn-sm btn-outline-secondary pagination-btn"
                            disabled={validCurrentPage === totalPages}
                            onClick={() => {
                                setCurrentPage(prev => Math.min(totalPages, prev + 1));
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        >
                            Suivant &raquo;
                        </button>
                    </div>
                )}
            </div>

            {isAdmin && selectedIds.length > 0 && (
                isBulkMinimized ? (
                    <div
                        className="bulk-minimized-pill animate__animated animate__fadeInUp"
                        onClick={() => setIsBulkMinimized(false)}
                        title="Cliquer pour afficher les actions groupées"
                    >
                        <span className="count">{selectedIds.length}</span>
                        <span className="label">{selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}</span>
                        <FaChevronUp size={11} className="ms-1 opacity-75" />
                        <button
                            type="button"
                            className="btn-bulk-icon-btn ms-1"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedIds([]);
                            }}
                            title="Tout désélectionner"
                        >
                            <FaTimes size={10} />
                        </button>
                    </div>
                ) : (
                    <div className="bulk-action-bar animate__animated animate__slideInUp">
                        <div className="bulk-info">
                            <span className="count">{selectedIds.length}</span>
                            <span className="label">{selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="bulk-actions">
                            <select
                                className="form-select form-select-sm bulk-select-doc"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setBulkDocType(e.target.value);
                                        setShowBulkModal(true);
                                        e.target.value = '';
                                    }
                                }}
                                defaultValue=""
                                title="Générer des documents pour les cas sélectionnés"
                            >
                                <option value="" disabled>🖨️ Documents ({selectedIds.length})...</option>
                                <option value="BL">🚚 Bons de Livraison ({selectedIds.length})</option>
                                <option value="WORKSHEET">📋 Fiches de Travail ({selectedIds.length})</option>
                                <option value="PATIENT">👤 Fiches Patient ({selectedIds.length})</option>
                                <option value="QUOTE">📄 Devis ({selectedIds.length})</option>
                                <option value="INVOICE">💶 Factures ({selectedIds.length})</option>
                            </select>

                            <select
                                className="form-select form-select-sm bulk-select-status"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleBulkStatusChange(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                                defaultValue=""
                                title="Changer le statut des cas sélectionnés"
                            >
                                <option value="" disabled>Statut...</option>
                                <option value="Reçu">📥 Reçu</option>
                                <option value="Production">⚙️ Production</option>
                                <option value="En attente">⏳ En attente</option>
                                <option value="Livré">🚚 Livré</option>
                                <option value="Facturé">💶 Facturé</option>
                            </select>

                            <button className="btn-bulk-delete" onClick={handleBulkDelete} title="Supprimer les cas sélectionnés">
                                <FaTrashAlt size={11} /> Supprimer
                            </button>

                            <button 
                                className="btn-bulk-deselect"
                                onClick={() => setSelectedIds([])}
                                title="Tout désélectionner"
                            >
                                Annuler
                            </button>

                            <button
                                type="button"
                                className="btn-bulk-icon-btn"
                                onClick={() => setIsBulkMinimized(true)}
                                title="Réduire temporairement la barre"
                            >
                                <FaChevronDown size={11} />
                            </button>
                        </div>
                    </div>
                )
            )}
            {showBulkModal && (
                <BulkDocumentModal
                    orders={orders.filter(o => selectedIds.includes(o.id))}
                    products={products}
                    clients={clients}
                    docType={bulkDocType}
                    onClose={() => setShowBulkModal(false)}
                />
            )}
            {showScanListModal && (
                <CaseListScannerModal
                    isOpen={showScanListModal}
                    onClose={() => setShowScanListModal(false)}
                    orders={orders}
                    onApplySelection={(ids) => {
                        setSelectedIds(ids);
                    }}
                    onBulkStatusChange={async (status, ids) => {
                        try {
                            await Promise.all(ids.map(id => updateOrder(id, { status })));
                            setSelectedIds([]);
                        } catch (error) {
                            console.error("Erreur lors de la mise à jour des statuts:", error);
                            alert("Erreur lors de la mise à jour de certains cas.");
                        }
                    }}
                    onOpenBulkDocuments={(docType, ids) => {
                        setSelectedIds(ids);
                        setBulkDocType(docType);
                        setShowBulkModal(true);
                    }}
                />
            )}

            {/* Direct Case Chat Modal from LabWorkList */}
            {activeChatOrder && (
                <div className="labwork-chat-modal-overlay" onClick={() => setActiveChatOrder(null)}>
                    <div className="labwork-chat-modal-content" onClick={(e) => e.stopPropagation()}>
                        <CaseChat
                            orderId={activeChatOrder.id}
                            caseInfo={{
                                caseNumber: activeChatOrder.caseNumber,
                                patientName: activeChatOrder.patientName,
                                practitionerName: activeChatOrder.practitionerName
                            }}
                            onPreviousCase={handlePreviousCase}
                            onNextCase={handleNextCase}
                            hasPrevious={hasPreviousCase}
                            hasNext={hasNextCase}
                            currentCaseIndex={currentChatIndex >= 0 ? currentChatIndex : null}
                            totalCasesCount={casesWithMessagesList.length}
                            onMarkAsUnread={() => {
                                fetchMessagesAndUnreadCounts();
                            }}
                            onMessagesRead={(ordId) => {
                                setUnreadByCase(prev => {
                                    if (!prev[ordId]) return prev;
                                    const next = { ...prev };
                                    delete next[ordId];
                                    return next;
                                });
                            }}
                            onPendingReplyChange={(ordId, isPending) => {
                                setPendingReplyByCase(prev => ({ ...prev, [ordId]: isPending }));
                                updateOrder(ordId, { priority: isPending ? 'pending_reply' : 'normal' });
                            }}
                            onClose={() => setActiveChatOrder(null)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default LabWorkList;

